// src/App.tsx — Slice B routing scaffold.
//
// Routing shape:
//   /login, /register, /forgot-pin   — public (auth screens)
//   /, /apps, /docs, /me             — root authenticated screens (BottomNav visible)
//   /contacts, /contacts/:id, etc.   — inner-app screens (BottomNav hidden by MobileFrame)
//
// All authenticated routes share the MobileFrame layout, which renders the
// fixed BottomNav only on the four root screens. RequireAuth wraps the lot
// so an unauthenticated visitor never sees the frame — useCurrentUser
// redirects them to /login (PIN hint) or /.auth/login/aad (default SSO).

import { lazy } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { RequireAuth } from "./components/AuthGate";
import { MobileFrame } from "./components/MobileFrame";
import { useTheme } from "./hooks/useTheme";
import { ContactsPage } from "./pages/ContactsPage";
import { LoginScreen } from "./pages/LoginScreen";
import { RegisterScreen } from "./pages/RegisterScreen";
import { ForgotPinScreen } from "./pages/ForgotPinScreen";
import { HomePage } from "./pages/HomePage";
import { AppsPage } from "./pages/AppsPage";
import { MePage } from "./pages/MePage";
import { AlertsPage } from "./pages/AlertsPage";
import { DutyPage } from "./pages/DutyPage";
import { ReportsPage } from "./pages/ReportsPage";
import { CrewCallPage } from "./pages/CrewCallPage";
import { LiveDutyView } from "./pages/LiveDutyView";
// Lazy-loaded feature modules (code-split Phase 2). Each is App-only and loads
// on first navigation, behind the Suspense + ChunkErrorBoundary in MobileFrame.
const DocumentsModule = lazy(() => import("./modules/documents").then((m) => ({ default: m.DocumentsModule })));
const ConsentsModule = lazy(() => import("./modules/consents").then((m) => ({ default: m.ConsentsModule })));
const PeopleModule = lazy(() => import("./modules/people").then((m) => ({ default: m.PeopleModule })));
import { PersonalDetailsPage } from "./pages/PersonalDetailsPage";
import { ChangePinPage } from "./pages/ChangePinPage";
import { PrivacyPage } from "./pages/PrivacyPage";
import { PreferencesPage } from "./pages/PreferencesPage";
import { NotificationsPage } from "./pages/NotificationsPage";
import { OnboardingWizard } from "./pages/OnboardingWizard";
const WeeklyPage = lazy(() => import("./pages/WeeklyPage").then((m) => ({ default: m.WeeklyPage })));
import { TicketsPage } from "./pages/TicketsPage";
import { AccessUsersPage } from "./pages/AccessUsersPage";
import { AccessUserEditPage } from "./pages/AccessUserEditPage";
import { HrAuditsPage } from "./pages/HrAuditsPage";
import { LiveTrackerPage } from "./pages/LiveTrackerPage";
import { CamViewPage } from "./pages/CamViewPage";
import { CamViewProPage } from "./pages/CamViewProPage";
import { OutOfOfficePage } from "./pages/OutOfOfficePage";
import { SystemPreferencesPage } from "./pages/SystemPreferencesPage";
import { TimesheetPage } from "./pages/TimesheetPage";
import { GroupThreadPage } from "./pages/GroupThreadPage";
import { GroupManagerPage } from "./pages/GroupManagerPage";
import { SchedulerPage } from "./pages/scheduler/SchedulerPage";
import { ApAutoForwardPage } from "./pages/ap-autoforward/ApAutoForwardPage";
import { ComposeCalloutPage } from "./pages/firstaider/ComposeCalloutPage";
import { IncidentPage } from "./pages/firstaider/IncidentPage";
import { MemberIdPage } from "./pages/MemberIdPage";
import { IdCardPage } from "./pages/IdCardPage";
const LicenceCheckPage = lazy(() => import("./pages/licence/LicenceCheckPage").then((m) => ({ default: m.LicenceCheckPage })));
const ArticleLibrary = lazy(() => import("./pages/nlcreator/ArticleLibrary").then((m) => ({ default: m.ArticleLibrary })));
const IssuesList = lazy(() => import("./pages/nlcreator/IssuesList").then((m) => ({ default: m.IssuesList })));
const LayoutEditor = lazy(() => import("./pages/nlcreator/LayoutEditor").then((m) => ({ default: m.LayoutEditor })));
const ArchivedIssues = lazy(() => import("./pages/nlcreator/ArchivedIssues").then((m) => ({ default: m.ArchivedIssues })));
const NewsletterReader = lazy(() => import("./pages/NewsletterReader").then((m) => ({ default: m.NewsletterReader })));
const NewsletterListPage = lazy(() => import("./pages/NewsletterListPage").then((m) => ({ default: m.NewsletterListPage })));
const MoorslineListPage = lazy(() => import("./pages/moorsline/MoorslineListPage").then((m) => ({ default: m.MoorslineListPage })));
const MoorslineReader = lazy(() => import("./pages/moorsline/MoorslineReader").then((m) => ({ default: m.MoorslineReader })));
const MoorslineEditor = lazy(() => import("./pages/moorsline/editor/MoorslineEditor").then((m) => ({ default: m.MoorslineEditor })));
// Newsletter Reader (migration 124) — the members' newsletter as an uploaded-PDF
// magazine (a Moorsline sibling). Reader + list + publisher console are all
// inner-app screens with their own top bars.
const NewsletterReaderListPage = lazy(() => import("./pages/newsletter-reader/NewsletterReaderListPage").then((m) => ({ default: m.NewsletterReaderListPage })));
const NewsletterReaderView = lazy(() => import("./pages/newsletter-reader/NewsletterReaderView").then((m) => ({ default: m.NewsletterReaderView })));
const NewsletterMobileView = lazy(() => import("./pages/newsletter-reader/NewsletterMobileView").then((m) => ({ default: m.NewsletterMobileView })));
const NewsletterPublisher = lazy(() => import("./pages/newsletter-reader/editor/NewsletterPublisher").then((m) => ({ default: m.NewsletterPublisher })));
// Mini-Blog (migration 128) — department-scoped micro-articles: stream reader
// + the Author (creator) surface. Both inner-app screens with their own top bars.
const MiniBlogPage = lazy(() => import("./pages/miniblog/MiniBlogPage").then((m) => ({ default: m.MiniBlogPage })));
const MiniBlogCreatorPage = lazy(() => import("./pages/miniblog/MiniBlogCreatorPage").then((m) => ({ default: m.MiniBlogCreatorPage })));
// Phase 5d — CEO Newsletter builder + reader (default exports).
const CeoBuilder = lazy(() => import("./pages/nlcreator/ceo/CeoBuilder"));
const CeoNewsletterReader = lazy(() => import("./pages/CeoNewsletterReader"));
const ArticleWriterPage = lazy(() => import("./pages/ArticleWriterPage").then((m) => ({ default: m.ArticleWriterPage })));
import { CalendarPage } from "./pages/calendar/CalendarPage";
// Issue Manager — full-screen desktop module, lazy-loaded (code-split). ONE
// wildcard route (/issues/*) renders the module's own royal-blue sidebar shell
// + internal routing (Dashboard, Issues, Team, Reports, S&T Map, SLA Tuning,
// Smart Tags, Who-can-report, My notifications, Admin). The reporter app (Raise
// an Issue) is a separate persona kept at /raise-issue. Suspense +
// ChunkErrorBoundary live in MobileFrame. See docs/build-plan/bundle-code-splitting.md.
// Imported from concrete files, NOT the ./modules/issues barrel: the barrel
// re-exports both IssuesModule (the manager+admin console) and the reporter
// pages, so importing it for either pulls BOTH into one chunk. Splitting the
// entry points keeps the reporter (Raise an Issue, used by many volunteers) off
// the heavy console/admin/recharts payload.
const IssuesModule = lazy(() => import("./modules/issues/IssuesModule").then((m) => ({ default: m.IssuesModule })));
const RaiseIssuePage = lazy(() => import("./modules/issues/routes/RaiseIssuePage").then((m) => ({ default: m.RaiseIssuePage })));
const MyIssuesPage = lazy(() => import("./modules/issues/routes/MyIssuesPage").then((m) => ({ default: m.MyIssuesPage })));
const ReporterIssueThreadPage = lazy(() => import("./modules/issues/routes/ReporterIssueThreadPage").then((m) => ({ default: m.ReporterIssueThreadPage })));
const LongServicePass = lazy(() => import("./modules/long-service").then((m) => ({ default: m.LongServicePass })));
const LongServiceAdmin = lazy(() => import("./modules/long-service").then((m) => ({ default: m.LongServiceAdmin })));
const MyTraining = lazy(() => import("./modules/training").then((m) => ({ default: m.MyTraining })));
const TrainingAdmin = lazy(() => import("./modules/training").then((m) => ({ default: m.TrainingAdmin })));
const IncidentLogModule = lazy(() => import("./modules/incident-log").then((m) => ({ default: m.IncidentLogModule })));
const BigPlanDevicesPage = lazy(() => import("./pages/BigPlanDevicesPage").then((m) => ({ default: m.BigPlanDevicesPage })));
// Rostering (migration 147) — thin personal view onto rostering.nymr.co.uk for
// the signed-in volunteer. Self-gates on me.apps.rostering (like CamView); the
// /api/rostering/* proxy re-checks server-side. Off by default for everyone.
const RosteringPage = lazy(() => import("./pages/rostering/RosteringPage").then((m) => ({ default: m.RosteringPage })));

export default function App() {
  // Slice J: publish the user's theme tokens as CSS custom properties on the
  // root element. No-op for anonymous users; default skin until /api/me lands.
  useTheme();

  return (
    <Routes>
      {/* Public auth surfaces */}
      <Route path="/login" element={<LoginScreen />} />
      <Route path="/register" element={<RegisterScreen />} />
      <Route path="/forgot-pin" element={<ForgotPinScreen />} />

      {/* Authenticated tree — all routes wrapped in MobileFrame */}
      <Route
        element={
          <RequireAuth>
            <MobileFrame />
          </RequireAuth>
        }
      >
        {/* Root screens — BottomNav visible */}
        <Route path="/" element={<HomePage />} />
        <Route path="/apps" element={<AppsPage />} />
        <Route path="/me" element={<MePage />} />

        {/* Documents (rebuild, migration 079) — full-screen module under /docs/*.
            Library + detail + editor are managed internally; /docs/:id is the
            share-link target. Inner-app (removed from MobileFrame ROOT_PATHS). */}
        <Route path="/docs/*" element={<DocumentsModule />} />

        {/* Confirmations & Consents (onboarding, migration 080) — full-screen
            module under /consents/*. List + viewer managed internally; inner-app
            (removed from MobileFrame ROOT_PATHS so the bottom nav is hidden). */}
        <Route path="/consents/*" element={<ConsentsModule />} />

        {/* People — Onboarding & Offboarding (build-plan 38). Full-screen desktop
            HR module under /people/*; sidebar + tabs managed internally. Admin /
            HR-admin gated (the module self-gates). Inner-app (not a ROOT_PATH). */}
        <Route path="/people/*" element={<PeopleModule />} />

        {/* Incident Log (H&S, migration 101) — full-screen SSO-only control app
            under /incident-log/*; dashboard, list, record, tasks, messages routed
            internally. Self-gates on apps.incidentLog (Read Only) /
            isIncidentAdmin (Full Access). Inner-app (not a ROOT_PATH). */}
        <Route path="/incident-log/*" element={<IncidentLogModule />} />
        <Route path="/bigplan-devices" element={<BigPlanDevicesPage />} />

        {/* Inner-app screens — BottomNav auto-hidden by MobileFrame's path check */}
        <Route path="/contacts" element={<ContactsPage />} />
        <Route path="/contacts/:id" element={<ContactsPage />} />

        {/* Slice C admin pages */}
        <Route path="/alerts" element={<AlertsPage />} />
        <Route path="/duty" element={<DutyPage />} />
        <Route path="/reports" element={<ReportsPage />} />

        {/* Events Live (volunteer loop) — Crew Call (offer availability) + the
            dark Live Duty View. Inner-app full-screen routes; they ship their
            own top bar (no bottom nav). */}
        <Route path="/crew-call" element={<CrewCallPage />} />
        <Route path="/live-duty" element={<LiveDutyView />} />

        {/* Slice D me sub-tree */}
        <Route path="/me/details" element={<PersonalDetailsPage />} />
        <Route path="/me/pin" element={<ChangePinPage />} />
        <Route path="/me/privacy" element={<PrivacyPage />} />
        {/* Slice L / P5 — app preferences moved from an inline panel to a
            dedicated screen with explicit Save. */}
        <Route path="/me/preferences" element={<PreferencesPage />} />
        {/* CODE-701 (Phase 7 prototype) — notification preferences. Lives
            under /me alongside the other profile sub-pages. Mocked
            client-side until ENG-705 / ENG-706 ship the live endpoints. */}
        <Route path="/me/notifications" element={<NotificationsPage />} />

        {/* Slice F — self-claim member number */}
        <Route path="/me/member-id" element={<MemberIdPage />} />

        {/* Slice D onboarding wizard — auto-triggered by RequireAuth when
            hub_users.onboarded_at is null; also reachable directly from
            the MePage nudge after a Skip. */}
        <Route path="/onboarding" element={<OnboardingWizard />} />

        {/* Slice C documents retired — replaced by the /docs/* module above. */}

        {/* Slice E — Weekly Sheet + Comp Tickets */}
        <Route path="/weekly" element={<WeeklyPage />} />
        <Route path="/tickets" element={<TicketsPage />} />

        {/* Rostering (migration 147) — personal view onto rostering.nymr.co.uk.
            The page self-gates on me.apps.rostering (invisible + inaccessible to
            everyone but the granted owner until released in User Access). */}
        <Route path="/rostering" element={<RosteringPage />} />

        {/* Mini-Blog (migration 128) — stream reader + Author surface. */}
        <Route path="/miniblog" element={<MiniBlogPage />} />
        <Route path="/miniblog/creator" element={<MiniBlogCreatorPage />} />

        {/* ID Card — digital twin of the NYMR PVC ID card. Default app for
            every user; inner-app screen (its own TopBar + back button). */}
        <Route path="/idcard" element={<IdCardPage />} />

        {/* Long Service Awards - LS Travel Pass (recipient; gated by apps.longService). */}
        <Route path="/long-service" element={<LongServicePass />} />

        {/* LS Admin - administration console (desktop; gated by isLongServiceAdmin). */}
        <Route path="/long-service/admin" element={<LongServiceAdmin />} />

        {/* Training & Competencies (migration 098). My Training (recipient; gated by
            apps.training — shows once the user has a matched iHasco record). */}
        <Route path="/training" element={<MyTraining />} />
        {/* Training admin console (desktop; Admin / HR Admin). Wildcard for the
            internal register / unmatched / audit tabs. */}
        <Route path="/training/admin/*" element={<TrainingAdmin />} />

        {/* Driving Licence Check — one route; the page self-selects the driver
            (apps.drivingLicenceCheck) or DV Admin (isDvCheckAdmin) view. */}
        <Route path="/licence-check" element={<LicenceCheckPage />} />

        {/* Sign-In System (Build 1) — Sign In Timesheet (all users; admin sees
            all-users view) + System Preferences (SSO admin). The page bodies
            self-gate; routing here is the entry point. */}
        <Route path="/timesheet" element={<TimesheetPage />} />
        <Route path="/prefs" element={<SystemPreferencesPage />} />

        {/* Sign-In System (Build 2) — Group QuickMessage thread (user) +
            Group Manager (SSO admin). Page bodies self-gate; routing is the
            entry point. */}
        <Route path="/group-qm/:id" element={<GroupThreadPage />} />
        <Route path="/group-manager" element={<GroupManagerPage />} />

        {/* Info Hub Scheduler (SSO admin) — universal control surface for the
            background jobs. Page body self-gates; routing is the entry point. */}
        <Route path="/scheduler" element={<SchedulerPage />} />

        {/* AP Auto-Forward (SSO admin) — kill-switch + dry-run/live control for
            the experimental AP Invoice Auto-Forward Logic App. Page body
            self-gates; routing is the entry point. */}
        <Route path="/ap-autoforward" element={<ApAutoForwardPage />} />

        {/* Phase 8 — First Aider Callout. Compose (any authenticated user) +
            the shared incident thread. Both are inner-app screens (no bottom
            nav; they ship their own red FaTopBar). */}
        <Route path="/apps/first-aider" element={<ComposeCalloutPage />} />
        <Route path="/first-aider/incidents/:id" element={<IncidentPage />} />

        {/* Phase 9 / Cut 1 — Calendar (mock data; Cut 2 wires the API). */}
        <Route path="/calendar" element={<CalendarPage />} />

        {/* Slice F — admin user maintenance */}
        <Route path="/access/users" element={<AccessUsersPage />} />
        <Route path="/access/users/new" element={<AccessUserEditPage mode="new" />} />
        <Route path="/access/users/:id" element={<AccessUserEditPage mode="edit" />} />

        {/* HR Audits — admin / HR admin only (the page self-gates). */}
        <Route path="/hr-audits" element={<HrAuditsPage />} />

        {/* Live Tracker — GPS fleet health + live-map snapshot. Admin / HR
            admin only (the page self-gates; hr-audits precedent). */}
        <Route path="/live-tracker" element={<LiveTrackerPage />} />
        <Route path="/camview" element={<CamViewPage />} />
        <Route path="/camview-pro" element={<CamViewProPage />} />
        <Route path="/out-of-office" element={<OutOfOfficePage />} />

        {/* Slice H — Newsletter creator (admin) */}
        <Route path="/nlcreator" element={<IssuesList />} />
        <Route path="/nlcreator/articles" element={<ArticleLibrary />} />
        {/* Slice L / P6 — archived newsletters admin screen, must precede /:id */}
        <Route path="/nlcreator/archived" element={<ArchivedIssues />} />
        {/* Phase 5d — CEO Newsletter builder. Literal 'ceo' precedes /:id. */}
        <Route path="/nlcreator/ceo/:id" element={<CeoBuilder />} />
        <Route path="/nlcreator/:id" element={<LayoutEditor />} />
        {/* Slice B — Article Writer app (app-gated; off by default). */}
        <Route path="/article-writer" element={<ArticleWriterPage />} />

        {/* Slice H — Newsletter reader (public read once published) */}
        {/* Slice L / P1 — /newsletter list view, closes the gap that bounced
            the home quick-link tile back to / via the catch-all. */}
        <Route path="/newsletter" element={<NewsletterListPage />} />
        {/* Phase 5d — CEO Newsletter reader (recipient-gated). Precedes /:id. */}
        <Route path="/newsletter/ceo/:id" element={<CeoNewsletterReader />} />
        <Route path="/newsletter/:id" element={<NewsletterReader />} />

        {/* Moorsline — members' magazine. Server-rendered page images (no client
            PDF). List + reader are inner-app screens (own top bar). NOTE: when
            the editor console lands, its literal /moorsline/editor route MUST be
            registered BEFORE /moorsline/:id below. */}
        <Route path="/moorsline" element={<MoorslineListPage />} />
        <Route path="/moorsline/editor" element={<MoorslineEditor />} />
        <Route path="/moorsline/:id" element={<MoorslineReader />} />

        {/* Newsletter Reader (migration 124) — uploaded-PDF members' newsletter.
            Literal /newsletter-reader/editor MUST precede /newsletter-reader/:id. */}
        <Route path="/newsletter-reader" element={<NewsletterReaderListPage />} />
        <Route path="/newsletter-reader/editor" element={<NewsletterPublisher />} />
        <Route path="/newsletter-reader/:id/mobile" element={<NewsletterMobileView />} />
        <Route path="/newsletter-reader/:id" element={<NewsletterReaderView />} />

        {/* Issue Manager — full-screen desktop console (2026-06-20 rebuild).
            ONE wildcard route; the module owns its sidebar shell + internal
            routing (Dashboard, Issues queue + detail, Team, Reports, S&T Map,
            SLA Tuning, Smart Tags, Who-can-report, My notifications, Admin).
            Self-gates on me.issues; legacy flat URLs redirect internally. */}
        <Route path="/issues/*" element={<IssuesModule />} />
        {/* Raise an Issue — separate reporter persona, gated by the
            apps.raiseIssue User Access permission. (Legacy /issues/raise +
            /issues/mine redirect here from inside the module.) */}
        <Route path="/raise-issue" element={<RaiseIssuePage />} />
        <Route path="/raise-issue/mine" element={<MyIssuesPage />} />
        {/* Reporter thread view for an issue the user raised. Literal
            /raise-issue/mine above MUST precede this dynamic :id route. */}
        <Route path="/raise-issue/:id" element={<ReporterIssueThreadPage />} />

        {/* Future routes — placeholders until their slice ships */}
        <Route path="/trains" element={<Navigate to="/" replace />} />
        <Route path="/vacancies" element={<Navigate to="/" replace />} />
      </Route>

      {/* Anything else — bounce home (which will redirect through RequireAuth if needed). */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
